import React from "react";

type GlobalErrorBoundaryProps = {
  children: React.ReactNode;
};

type GlobalErrorBoundaryState = {
  hasError: boolean;
  errorMessage?: string;
  errorStack?: string;
  reportId?: string;
};

export class GlobalErrorBoundary extends React.Component<GlobalErrorBoundaryProps, GlobalErrorBoundaryState> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: unknown): GlobalErrorBoundaryState {
    const msg = error instanceof Error ? error.message : String(error);
    return { hasError: true, errorMessage: msg, reportId: `${Date.now()}` };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    // Log details for diagnostics (non-sensitive)
    console.error("GlobalErrorBoundary caught: ", error, info);
    const stack = error instanceof Error ? error.stack : undefined;
    const combined = [stack, info.componentStack].filter(Boolean).join('\n');
    this.setState({ errorStack: combined });
  }

  handleReset = () => {
    try {
      // Minimal reset without nuking auth: just reload
      window.location.reload();
    } catch {
      window.location.href = "/";
    }
  };

  handleHardReset = () => {
    try {
      const keysToKeep = Object.keys(localStorage).filter(
        (key) => key.startsWith('sb-') || key.includes('auth')
      );
      const saved: Record<string, string> = {};
      keysToKeep.forEach((key) => {
        const value = localStorage.getItem(key);
        if (value) saved[key] = value;
      });
      localStorage.clear();
      Object.entries(saved).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
    } catch (e) {
      console.warn('Hard reset failed:', e);
    } finally {
      this.handleReset();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground">
          <div className="max-w-md w-full border rounded-lg p-6 shadow-lg bg-card">
            <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
            <p className="text-sm text-muted-foreground mb-4">An unexpected error occurred. Please try reloading.</p>
            {this.state.reportId && (
              <p className="text-xs text-muted-foreground mb-4">Report ID: {this.state.reportId}</p>
            )}
            {(this.state.errorMessage || this.state.errorStack) && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm">Show technical details</summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">
                  {this.state.errorMessage}
                  {this.state.errorStack ? '\n' + this.state.errorStack : ''}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <button onClick={this.handleReset} className="inline-flex items-center justify-center rounded-md px-4 py-2 bg-primary text-primary-foreground hover:opacity-90">
                Reload
              </button>
              <button onClick={this.handleHardReset} className="inline-flex items-center justify-center rounded-md px-4 py-2 bg-destructive text-destructive-foreground hover:opacity-90">
                Reset app data (keeps login)
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
